/**
 * Sortie (/sortie) — écran de service le plus rapide : chercher → quantité → valider.
 * Sans photo. Recherche instantanée par référence, clavier ↑/↓/Entrée (Entrée à nouveau = valider),
 * tarif par motif, toast d'annulation 10 s, focus replacé après chaque validation.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ScanLine } from 'lucide-react';
import { sortieSearch, sortieUndo, sortieRecent } from '../../offline/store';
import { formatTime, formatSigned } from '../../utils/format';
import { Button, EmptyState } from '../../components/ui/kit';
import { WineColorDot } from '../../components/wine/wine';
import { SEARCH_DEBOUNCE_MS } from '../../constants';
import StockMovementPanel from '../cave/StockMovementPanel';

export default function Sortie() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);

  const [q, setQ] = useState('');
  const [data, setData] = useState({ results: [], frequent: true });
  const [highlight, setHighlight] = useState(0);
  const [selected, setSelected] = useState(location.state?.preselect || null);

  const { data: recent = [], refetch: refetchRecent } = useQuery({ queryKey: ['sortie-recent'], queryFn: sortieRecent });

  const focusSearch = useCallback(() => inputRef.current?.focus(), []);
  useEffect(() => { focusSearch(); }, [focusSearch]);

  // Recherche instantanée (debounce).
  useEffect(() => {
    const t = setTimeout(() => {
      sortieSearch(q).then((r) => { setData(r); setHighlight(0); }).catch(() => {});
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  const selectRef = (r) => { setSelected(r); };

  const onKeyDown = (e) => {
    const results = data.results;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (!selected && results[highlight]) selectRef(results[highlight]);
    } else if (e.key === 'Escape') { setQ(''); setSelected(null); }
  };

  // Réinitialise la recherche après un mouvement validé dans le volet.
  const afterMovement = () => { setSelected(null); setQ(''); refetchRecent(); setTimeout(focusSearch, 50); };

  return (
    <div className="p-4 md:p-6 grid lg:grid-cols-[1fr_380px] gap-6">
      {/* Colonne recherche */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="kicker">Sortir une bouteille</p>
          <Link to="/scanner" className="vd-link text-[13px] flex items-center gap-1" data-testid="scan-instead">
            <ScanLine className="w-4 h-4" strokeWidth={1.6} /> Scanner l'étiquette à la place
          </Link>
        </div>
        <input
          ref={inputRef}
          data-testid="sortie-search"
          className="vd-input w-full"
          style={{ height: 56, fontSize: 17 }}
          placeholder={'Tapez quelques lettres : « reine », « reine 19 mag », « coquin »…'}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <div className="flex items-center justify-between mt-2 text-[12.5px] text-n500">
          <span data-testid="sortie-count">{data.frequent ? 'Sorties fréquentes ce soir' : `${data.results.length} références en stock`}</span>
          <span>↑ ↓ pour choisir · Entrée pour sélectionner, Entrée à nouveau pour valider</span>
        </div>

        <div className="mt-3">
          {data.results.length === 0 ? (
            selected ? null : (
            <EmptyState title="Aucune bouteille" description="Aucune bouteille en stock ne correspond. Vérifiez l'orthographe ou scannez l'étiquette."
              action={<Button variant="primary" onClick={() => navigate('/scanner')}>Scanner l'étiquette</Button>} testId="sortie-empty" />
            )
          ) : (
            <div className="vd-card overflow-hidden">
              {data.results.map((r, i) => (
                <button key={r.reference_id} data-testid={`sortie-result-${r.reference_id}`}
                  onMouseEnter={() => setHighlight(i)} onClick={() => selectRef(r)}
                  className={`w-full flex items-center gap-3 px-3 hair-b last:border-b-0 text-left ${i === highlight ? 'vd-selected' : 'hover:bg-surface'}`}
                  style={{ minHeight: 58 }}>
                  <WineColorDot color={r.color} />
                  <span className="flex-1 min-w-0">
                    <span className="font-title text-lg leading-tight">{r.name} {r.year}</span>
                    <span className="block text-[12px] text-n500">{r.domain_name}</span>
                  </span>
                  <span className="text-[13px] text-n600 w-24 nums">{r.format_label}</span>
                  <span className="text-[12px] text-n500 w-20 nums">{r.location_code}</span>
                  <span className={`w-10 text-right nums ${r.quantity <= 2 ? 'text-gold700' : ''}`}>{r.quantity}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Colonne référence choisie */}
      <div className="lg:border-l lg:border-hair lg:pl-6">
        {!selected ? (
          <div className="text-n500 text-[14px] pt-10 text-center" data-testid="no-selection">
            Sélectionnez une référence pour la sortir.
          </div>
        ) : (
          <StockMovementPanel cuveeId={selected.cuvee_id} initialRefId={selected.reference_id} mode="sortie" onDone={afterMovement} />
        )}

        {/* Mes dernières sorties */}
        <div className="mt-8">
          <p className="kicker mb-2">Mes dernières sorties</p>
          {recent.length === 0 ? (
            <p className="text-[13px] text-n500">Aucune sortie récente.</p>
          ) : (
            <div>
              {recent.map((m) => (
                <div key={m.movement_id} className="flex items-center gap-2 py-2 hair-b text-[13px]" data-testid={`recent-${m.movement_id}`}>
                  <span className="text-n500 nums w-12">{formatTime(m.date)}</span>
                  <span className="flex-1 min-w-0 truncate">{formatSigned(m.quantity)} {m.label} · {m.motif_label}</span>
                  {m.undoable && (
                    <button className="vd-link" data-testid={`recent-undo-${m.movement_id}`}
                      onClick={() => sortieUndo(m.movement_id).then(() => { qc.invalidateQueries({ queryKey: ['stock'] }); refetchRecent(); })}>
                      Annuler
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
